import React, { useMemo, useState, useEffect } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  CircleMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

/**
 * RouteAnalysis (simple absolute URLs for map fetches)
 *
 * Endpoints:
 *  - POST https://api.pharmacegroup.co.uk/routeanalysis_list
 *  - POST https://api.pharmacegroup.co.uk/routeanalysis_detail
 *  - POST https://api.pharmacegroup.co.uk/pharmacyLatLong
 *  - POST https://api.pharmacegroup.co.uk/driverroutesummary
 */

// ---- Helpers ----
const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return "";
  const [y, m, d] = String(yyyyMmDd).split("-");
  return `${pad2(d)}/${pad2(m)}/${y}`;
};
const secondsToHM = (s) => {
  const S = Math.max(0, Math.floor(Number(s) || 0));
  const hh = Math.floor(S / 3600);
  const mm = Math.floor((S % 3600) / 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};

// Absolute URLs for list/detail
const LIST_URL = "https://api.pharmacegroup.co.uk/routeanalysis_list";
const DETAIL_URL = "https://api.pharmacegroup.co.uk/routeanalysis_detail";

const RouteAnalysis = ({ initialPharmacyName } = {}) => {
  const token = localStorage.getItem("authToken");
  const masked = useMaskNames();

  // Pharmacies from localStorage (alphabetical)
  const storedMembers = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("members") || "[]");
    } catch {
      return [];
    }
  }, []);
  const members = useMemo(() => {
    const m = Array.isArray(storedMembers) ? [...storedMembers] : [];
    m.sort((a, b) =>
      String(a?.pharmacyName || "").localeCompare(String(b?.pharmacyName || ""))
    );
    return m;
  }, [storedMembers]);

  // Filters
  const [pharmacyChoice, setPharmacyChoice] = useState(initialPharmacyName || "ALL");
  const [month, setMonth] = useState("ALL");
  const [year, setYear] = useState("ALL");

  // Efficiency + dwell filters
  const [efficiencyMin, setEfficiencyMin] = useState(""); // client-side filtering
  const [dwellMin, setDwellMin] = useState(""); // sent to backend as clusterDurMin

  // Pagination + sorting
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 200;
  const [sortState, setSortState] = useState({ by: "date", dir: "desc" }); // 'date'|'deliveries'|'eff'

  // Distance unit toggle: route distances are stored in KM; clicking the
  // unit on the "Predicted distance" label switches the display between
  // kilometres and miles. The choice persists per browser (localStorage).
  const [distanceUnit, setDistanceUnit] = useState(
    () => (localStorage.getItem("route_distance_unit") === "mi" ? "mi" : "km")
  );
  const toggleDistanceUnit = () =>
    setDistanceUnit((u) => {
      const next = u === "km" ? "mi" : "km";
      localStorage.setItem("route_distance_unit", next);
      return next;
    });
  // Convert a stored-km value for display in the chosen unit (1 dp).
  const displayDistance = (km) => {
    if (km === null || km === undefined || km === "") return null;
    const n = Number(km);
    if (!Number.isFinite(n)) return null;
    const v = distanceUnit === "mi" ? n * 0.621371 : n;
    return Math.round(v * 10) / 10;
  };

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Detail
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Map modal
  const [showMap, setShowMap] = useState(false);
  const [mapOptions, setMapOptions] = useState({
    deliveries: true,
    actual: false,
    stationary: false,
  });
  const [mapKey, setMapKey] = useState(0);
  const [mapPoints, setMapPoints] = useState([]);
  const [mapData, setMapData] = useState({
    pharmacy: null,
    driverLogs: [], // array of [lat, long]
  });

  // Driver location (per-delivery) modal
  const [showDriverLocModal, setShowDriverLocModal] = useState(false);
  const [driverLocLoading, setDriverLocLoading] = useState(false);
  const [driverLocError, setDriverLocError] = useState("");
  const [driverLocData, setDriverLocData] = useState({
    stopLabel: null,
    outcome: null,
    deliveredAt: null,
    patientCoords: null, // [lat, long] — the patient's known address (reference pin)
    driverPoints: [], // [{lat, long, createdAt}] — up to 5 driver_logs points around the delivery
    deliveryLogPoint: null, // {lat, long} — recorded directly on the delivery_logs doc, if present
  });

  const selectedPharmacy =
    members.find((m) => m.pharmacyName === pharmacyChoice) || null;

// Read organisation name exactly from localStorage
const getGroupName = () => {
  try {
    const s = localStorage.getItem("organisationName");
    return s ? String(s).trim() : null;
  } catch {
    return null;
  }
};

// Pharmacy IDs to send to the backend
const getPharmacyIdsForRequest = () => {
  const groupName = (getGroupName() || "").toLowerCase();

  // Special case: organisation is "Pharmace all" AND UI shows ALL → ask backend for everything
  if (pharmacyChoice === "ALL" && groupName === "pharmace all") {
    return "xxx";
  }

  // Normal ALL → send every member pharmacyId from localStorage
  if (pharmacyChoice === "ALL") {
    const ids = (members || [])
      .map(m => (m?.pharmacyId != null ? String(m.pharmacyId) : ""))
      .filter(Boolean);
    return Array.from(new Set(ids));
  }

  // Specific pharmacy selected
  const pid = selectedPharmacy?.pharmacyId;
  return pid ? [String(pid)] : [];
};


  // ---- API helpers ----
  const apiPostAbs = async (url, body) => {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body || {}),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`${url} HTTP ${res.status} ${txt}`);
    }
    return res.json();
  };

  const loadList = async () => {
    setLoading(true);
    setError("");
       setItems([]);
    setSelectedDoc(null);
    try {
      const body = {
        pharmacyIds: getPharmacyIdsForRequest(),
        month: month === "ALL" ? null : Number(month),
        year: year === "ALL" ? null : Number(year),
        ratioMin: null, // not using hop ratio on backend anymore
        clusterDurMin: dwellMin === "" ? null : Number(dwellMin),
        page,
        limit: PAGE_SIZE,
      };
      const data = await apiPostAbs(LIST_URL, body);
      let list = Array.isArray(data?.items) ? data.items : [];

      // Client-side filter: Efficiency ratio >
      if (efficiencyMin !== "") {
        const min = Number(efficiencyMin);
        if (Number.isFinite(min)) {
          list = list.filter((it) => {
            const agg2 = it?.results?.aggregates || {};
            const totalS2 = Number(agg2?.totalActualSecondsForRoute || 0);
            const eff2 = Number(agg2?.efficiencyActualVsPredicted);
            if (!Number.isFinite(eff2) || totalS2 < 5 * 60) return false;
            return eff2 > min;
          });
        }
      }

      setItems(list);
      setTotal(Number(data?.total || list.length));
      if (list.length) {
        const first = list[0];
        const key = `${first.deliveryDate}|${first.deliveryRoundId}`;
        setSelectedKey(key);
        loadDetail(first.deliveryDate, first.deliveryRoundId);
      } else {
        setSelectedKey(null);
        setSelectedDoc(null);
      }
    } catch (e) {
      setError(e?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (deliveryDate, deliveryRoundId) => {
    setDetailLoading(true);
    setSelectedDoc(null);
    try {
      const data = await apiPostAbs(DETAIL_URL, {
        deliveryDate,
        deliveryRoundId,
      });
      setSelectedDoc(data?.item || null);
    } catch (e) {
      setError(e?.message || "Failed to load detail");
      setSelectedDoc(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadList(); /* initial */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-reload on pharmacy change
  useEffect(() => {
    setPage(1);
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pharmacyChoice]);

  // Derived (right pane)
  const agg = selectedDoc?.results?.aggregates || {};
  const hops = selectedDoc?.results?.hops || [];
  const clusters = selectedDoc?.results?.stationaryClusters || [];
  const perStop = selectedDoc?.results?.perStop || [];
  const deliveriesSummary = selectedDoc?.results?.deliveries || {};
  const perStopBySeq = useMemo(() => {
    const map = new Map();
    (perStop || []).forEach((s) => map.set(Number(s.seq), s));
    return map;
  }, [perStop]);

  const firstDeliveredAt = selectedDoc?.roundSummary?.firstDeliveredAt
    ? new Date(selectedDoc.roundSummary.firstDeliveredAt)
    : null;

  // Get last deliveredAt among stops (use the max by time if present)
  const lastDeliveredAt = useMemo(() => {
    const times = (perStop || [])
      .map((s) => (s?.deliveredAt ? new Date(s.deliveredAt) : null))
      .filter(Boolean);
    if (!times.length) return null;
    return new Date(Math.max(...times.map((t) => t.getTime())));
  }, [perStop]);

  // Helper to compute predicted seconds for a hop (adds +120 except last→pharmacy)
  const predictedSecsForHop = (h) => {
    if (typeof h?.predictedSeconds === "number") return h.predictedSeconds;
    const base = Number(h?.osrmDurationS || 0);
    return Math.max(0, Math.floor(base + (h?.isLastToPharmacy ? 0 : 120)));
  };

  // Optional actual seconds for pharmacy legs if backend provides them
  const pharmacyToFirstActualSeconds = Number(
    selectedDoc?.roundSummary?.pharmacyToFirstActualSeconds ??
      selectedDoc?.roundSummary?.pharmacyToFirstTimeSeconds ??
      NaN
  );
  const lastToPharmacyActualSeconds = Number(
    selectedDoc?.roundSummary?.lastToPharmacyActualSeconds ?? NaN
  );

  // Compute "actual secs" per hop
  const actualSecsForHop = (h) => {
    // Last drop → Pharmacy
    if (h?.isLastToPharmacy) {
      if (Number.isFinite(lastToPharmacyActualSeconds)) {
        return Math.max(0, Math.floor(lastToPharmacyActualSeconds));
      }
      return null;
    }

    const fromSeqNum = Number(h?.fromSeq);
    const toSeqNum = Number(h?.toSeq);

    // Between two drops: TO.deliveredAt - FROM.deliveredAt
    if (
      Number.isFinite(fromSeqNum) &&
      fromSeqNum >= 0 &&
      Number.isFinite(toSeqNum) &&
      toSeqNum >= 0
    ) {
      const fromStop = perStopBySeq.get(fromSeqNum);
      const toStop = perStopBySeq.get(toSeqNum);
      if (fromStop?.deliveredAt && toStop?.deliveredAt) {
        const s =
          (new Date(toStop.deliveredAt) - new Date(fromStop.deliveredAt)) /
          1000;
        return Math.max(0, Math.floor(s));
      }
      return null;
    }

    // Pharmacy → first drop
    if (
      h?.isPharmacyToFirst ||
      String(h?.fromLabel ?? h?.fromSeq) === "0" ||
      Number(h?.fromSeq) === -1
    ) {
      if (Number.isFinite(pharmacyToFirstActualSeconds)) {
        return Math.max(0, Math.floor(pharmacyToFirstActualSeconds));
      }
      // No depart timestamp; fall back to predicted so the column isn't blank
      return predictedSecsForHop(h);
    }

    return null;
  };

  // Find the last hop (last drop → Pharmacy)
  const lastHop = useMemo(
    () => (hops || []).find((h) => h?.isLastToPharmacy),
    [hops]
  );
  const lastHopPredictedSecs = useMemo(
    () => (lastHop ? predictedSecsForHop(lastHop) : 0),
    [lastHop]
  );

  // Compute a fabricated final time and adjusted total seconds if we have lastDeliveredAt
  const fabricatedFinalDate = useMemo(() => {
    if (!lastDeliveredAt || !lastHopPredictedSecs) return null;
    const d = new Date(lastDeliveredAt);
    d.setSeconds(d.getSeconds() + lastHopPredictedSecs);
    return d;
  }, [lastDeliveredAt, lastHopPredictedSecs]);

  // Adjusted total actual seconds for route (firstDeliveredAt → fabricated final)
  const adjustedTotalActualSeconds = useMemo(() => {
    if (firstDeliveredAt && fabricatedFinalDate) {
      const diff =
        (fabricatedFinalDate.getTime() - firstDeliveredAt.getTime()) / 1000;
      return diff > 0 ? Math.floor(diff) : 0;
    }
    return Number(agg?.totalActualSecondsForRoute || 0);
  }, [firstDeliveredAt, fabricatedFinalDate, agg?.totalActualSecondsForRoute]);

  const tooShort =
    adjustedTotalActualSeconds > 0 && adjustedTotalActualSeconds < 5 * 60;

  // ---- Styles ----
  const cellPad = { padding: "6px 8px", textAlign: "left", color: "#000" };
  const th = { ...cellPad, fontWeight: 700, background: "#f2f2f2" };

  // Left list helpers
  const getDeliveriesCount = (it) => {
    const succ = Number(it?.results?.deliveries?.successCount || 0);
    const fail = Number(it?.results?.deliveries?.failCount || 0);
    const total2 = succ + fail;
    return total2 || 0;
  };
  const getEfficiencyDisplay = (it) => {
    const totalS2 = Number(
      it?.results?.aggregates?.totalActualSecondsForRoute || 0
    );
    const eff = it?.results?.aggregates?.efficiencyActualVsPredicted;
    if (!eff || totalS2 < 5 * 60) return "n/a";
    return String(eff);
  };

  // Sorting (client-side on current page)
  const sortedItems = useMemo(() => {
    const arr = [...items];
    const { by, dir } = sortState;
    const cmp = (a, b) => {
      if (by === "deliveries") {
        return getDeliveriesCount(a) - getDeliveriesCount(b);
      }
      if (by === "eff") {
        const aShort =
          (a?.results?.aggregates?.totalActualSecondsForRoute || 0) < 300;
        const bShort =
          (b?.results?.aggregates?.totalActualSecondsForRoute || 0) < 300;
        const ea = aShort
          ? -Infinity
          : a?.results?.aggregates?.efficiencyActualVsPredicted ?? -Infinity;
        const eb = bShort
          ? -Infinity
          : b?.results?.aggregates?.efficiencyActualVsPredicted ?? -Infinity;
        return ea - eb;
      }
      return String(a.deliveryDate).localeCompare(String(b.deliveryDate));
    };
    arr.sort(cmp);
    if (dir === "desc") arr.reverse();
    return arr;
  }, [items, sortState]);

  const toggleSort = (col) => {
    setSortState((prev) => {
      if (prev.by === col)
        return { by: col, dir: prev.dir === "asc" ? "desc" : "asc" };
      return { by: col, dir: col === "date" ? "desc" : "asc" };
    });
  };

  // Map helpers
  const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
      if (points && points.length) {
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    }, [points, map]);
    return null;
  };

  // House icon for pharmacy
  const houseIcon = React.useMemo(() => {
    const svg = encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24">
        <path d="M12 3l9 8-1.4 1.4L18 10.3V20a1 1 0 0 1-1 1h-4v-6H11v6H7a1 1 0 0 1-1-1v-9.7l-1.6 2.1L3 11l9-8z" fill="#111" stroke="#fff" stroke-width="1"/>
      </svg>
    `);
    return L.icon({
      iconUrl: `data:image/svg+xml;utf8,${svg}`,
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      popupAnchor: [0, -28],
    });
  }, []);

  // Flexible accessors for logs (unused here but kept for clarity)
  const getLat = (o) => {
    const v = o?.lat ?? o?.latitude ?? o?.coords?.lat ?? o?.location?.lat;
    return typeof v === "number" ? v : undefined;
  };
  const getLon = (o) => {
    const v =
      o?.long ??
      o?.lng ??
      o?.lon ??
      o?.longitude ??
      o?.coords?.lon ??
      o?.location?.lon;
    return typeof v === "number" ? v : undefined;
  };

  const openMap = async () => {
    try {
      const dbName = (selectedDoc?.meta?.dbName || "").toString().trim();
      const theDay = String(selectedDoc?.deliveryDate || ""); // YYYY-MM-DD
      const roundId = String(selectedDoc?.deliveryRoundId || "");

      let pharmacy = null;
      let actualPath = [];

      // 1) Pharmacy coords
      if (dbName) {
        try {
          const res = await fetch(
            "https://api.pharmacegroup.co.uk/pharmacyLatLong",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ pharmacyId: dbName }),
            }
          );
          if (res.ok) {
            const payload = await res.json().catch(() => null);
            if (
              payload &&
              typeof payload.lat === "number" &&
              typeof payload.long === "number"
            ) {
              pharmacy = { lat: payload.lat, long: payload.long };
            }
          }
        } catch (e) {
          // ignore
        }
      }

      // 2) Driver logs (summary) -> actual route polyline
      if (dbName && theDay && roundId) {
        try {
          const [yStr, mStr] = theDay.split("-");
          const body = {
            pharmacyId: dbName,
            month: Math.max(0, Number(mStr) - 1), // 0..11
            year: Number(yStr),
          };

          const res = await fetch(
            "https://api.pharmacegroup.co.uk/driverroutesummary",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(body),
            }
          );

          if (res.ok) {
            const payload = await res.json().catch(() => null);
            const raw = Array.isArray(payload?.routes) ? payload.routes : [];

            const getLatLocal = (o) => o?.lat;
            const getLonLocal = (o) => o?.long;
            const getTimeLocal = (o) => o?.createdAt;
            const sameDayLocal = (isoLike, day) => {
              const d = isoLike ? new Date(isoLike) : null;
              return !!(d && d.toISOString().slice(0, 10) === day);
            };
            const matchesRoundLocal = (o, rId) =>
              String(o?.deliveryRoundId ?? "") === String(rId ?? "");

            const pts = raw
              .filter((r) => matchesRoundLocal(r, roundId))
              .filter((r) => sameDayLocal(getTimeLocal(r), theDay))
              .map((r) => {
                const la = getLatLocal(r);
                const lo = getLonLocal(r);
                const t = getTimeLocal(r);
                return typeof la === "number" && typeof lo === "number"
                  ? [la, lo, t]
                  : null;
              })
              .filter(Boolean)
              .sort((a, b) => new Date(a[2]) - new Date(b[2]))
              .map((x) => [x[0], x[1]]);

            if (pts.length >= 2) actualPath = pts;
          }
        } catch (e) {
          // ignore
        }
      }

      // 3) Fit bounds + open modal
      const deliveryPts = (selectedDoc?.results?.perStop || [])
        .map((s) => [s?.coords?.lat, s?.coords?.lon])
        .filter((a) => a[0] && a[1]);

      const allPoints = [
        ...deliveryPts,
        ...actualPath,
        ...(pharmacy ? [[pharmacy.lat, pharmacy.long]] : []),
      ];

      setMapData({ pharmacy, driverLogs: actualPath });
      setMapPoints(allPoints);
      setShowMap(true);
      setMapKey((k) => k + 1);
    } catch (e) {
      setShowMap(true);
    }
  };

  // Fetch driver_logs (movement) + delivery_logs' own lat/long for ONE delivery,
  // so we can confirm where the driver actually was when they tapped
  // "Delivered" / "Failed" for that stop.
  const openDriverLocationForStop = async (stop) => {
    if (!stop) return;

    setDriverLocError("");
    setDriverLocLoading(true);
    setShowDriverLocModal(true);
    setDriverLocData({
      stopLabel: Number.isInteger(stop.seq) ? Number(stop.seq) + 1 : null,
      outcome: typeof stop.success === "boolean" ? (stop.success ? "Success" : "Failure") : "—",
      deliveredAt: stop.deliveredAt || null,
      patientCoords:
        stop?.coords?.lat && stop?.coords?.lon ? [stop.coords.lat, stop.coords.lon] : null,
      driverPoints: [],
      deliveryLogPoint: null,
    });

    try {
      const dbName = (selectedDoc?.meta?.dbName || "").toString().trim();
      const driverId = selectedDoc?.meta?.driverId || null;
      const deliveryRoundId = String(selectedDoc?.deliveryRoundId || "");
      const patientId = stop?.patientId || null;
      const deliveredAt = stop?.deliveredAt || null;

      if (!dbName || !deliveredAt) {
        throw new Error("Missing pharmacy or delivery time for this stop.");
      }

      const payload = await apiPostAbs(
        "https://api.pharmacegroup.co.uk/driverLocationAtDelivery",
        { dbName, driverId, deliveryRoundId, patientId, deliveredAt }
      );

      const driverPoints = Array.isArray(payload?.driverPoints) ? payload.driverPoints : [];
      const deliveryLogPoint =
        payload?.deliveryLogPoint &&
        typeof payload.deliveryLogPoint.lat === "number" &&
        typeof payload.deliveryLogPoint.long === "number"
          ? [payload.deliveryLogPoint.lat, payload.deliveryLogPoint.long]
          : null;

      setDriverLocData((prev) => ({ ...prev, driverPoints, deliveryLogPoint }));

      if (!driverPoints.length && !deliveryLogPoint) {
        setDriverLocError("No driver location data found around this delivery.");
      }
    } catch (e) {
      setDriverLocError(e?.message || "Failed to load driver location.");
    } finally {
      setDriverLocLoading(false);
    }
  };

  const canPrev = page > 1;
  const canNext = page * PAGE_SIZE < total;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "48% 52%",
        gap: 12,
        height: "85vh",
        padding: 10,
      }}
    >
      {/* LEFT */}
      <div
        style={{
          overflowY: "auto",
          background: "#fff",
          color: "#000",
          padding: 10,
          borderRadius: 10,
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ marginTop: 0, color: "#000" }}>Route analysis</h3>
          <MaskToggleButton />
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "end",
            marginBottom: 10,
            color: "#000",
          }}
        >
          <label style={{ fontSize: 12, color: "#000" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Pharmacy</div>
            <select
              value={pharmacyChoice}
              onChange={(e) => setPharmacyChoice(e.target.value)}
              style={{
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 8px",
                minWidth: 220,
                color: "#000",
                background: "#fff",
              }}
            >
              <option value="ALL">All pharmacies</option>
              {members.map((m) => (
                <option key={m.pharmacyId} value={m.pharmacyName}>
                  {displayName(m.pharmacyName, masked)}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, color: "#000" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Month</div>
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 8px",
                color: "#000",
                background: "#fff",
              }}
            >
              <option value="ALL">All months</option>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i} value={i}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 12, color: "#000" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Year</div>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={{
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 8px",
                color: "#000",
                background: "#fff",
              }}
            >
              <option value="ALL">All years</option>
              {Array.from({ length: 10 }, (_, i) => {
                const yr = new Date().getFullYear() - i;
                return (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                );
              })}
            </select>
          </label>

          {/* Efficiency ratio filter (client-side) */}
          <label style={{ fontSize: 12, color: "#000" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Efficiency ratio &gt;
            </div>
            <input
              type="number"
              step="0.1"
              placeholder="(none)"
              value={efficiencyMin}
              onChange={(e) => setEfficiencyMin(e.target.value)}
              style={{
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 8px",
                width: 120,
                color: "#000",
                background: "#fff",
              }}
              title="Filter routes where overall Efficiency (actual/predicted) is greater than this value"
            />
          </label>

          {/* Dwell times -> backend clusterDurMin */}
          <label style={{ fontSize: 12, color: "#000" }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Dwell times &gt;
            </div>
            <input
              type="number"
              step="1"
              placeholder="(none)"
              value={dwellMin}
              onChange={(e) => setDwellMin(e.target.value)}
              style={{
                border: "1px solid #ccc",
                borderRadius: 6,
                padding: "6px 8px",
                width: 120,
                color: "#000",
                background: "#fff",
              }}
              title="Limit to routes that have at least one stationary episode longer than this many minutes"
            />
          </label>

          <button
            onClick={() => {
              setPage(1);
              loadList();
            }}
            style={{
              marginLeft: "auto",
              background: "#4f46e5",
              color: "white",
              border: 0,
              padding: "8px 14px",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Load
          </button>
        </div>

        {loading && <div style={{ fontSize: 12, color: "#111" }}>Loading…</div>}
        {error && <div style={{ fontSize: 12, color: "crimson" }}>{error}</div>}

        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: 8,
            background: "#fff",
            overflow: "hidden",
            color: "#000",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "120px 1fr 140px 170px 130px",
              gap: 8,
              padding: 8,
              fontSize: 13,
              fontWeight: 700,
              background: "#f2f2f2",
              borderBottom: "1px solid #eee",
              color: "#000",
            }}
          >
            <div style={{ cursor: "pointer" }} onClick={() => toggleSort("date")}>
              Date
            </div>
            <div>Pharmacy</div>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => toggleSort("deliveries")}
            >
              Deliveries
            </div>
            <div>Driver</div>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => toggleSort("eff")}
              title="Efficiency actual/predicted for route"
            >
              Efficiency
            </div>
          </div>
          <div style={{ maxHeight: "56vh", overflowY: "auto" }}>
            {sortedItems.map((it) => {
              const key = `${it.deliveryDate}|${it.deliveryRoundId}`;
              const active = key === selectedKey;
              return (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedKey(key);
                    loadDetail(it.deliveryDate, it.deliveryRoundId);
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr 140px 170px 130px",
                    gap: 8,
                    width: "100%",
                    textAlign: "left",
                    padding: 10,
                    fontSize: 13,
                    color: "#000",
                    background: active ? "#dbeafe" : "#fff",
                    border: 0,
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    fontWeight: active ? 700 : 500,
                    borderLeft: active
                      ? "4px solid #3b82f6"
                      : "4px solid transparent",
                  }}
                  title={it.deliveryRoundId}
                >
                  <div>{fmtDate(it.deliveryDate)}</div>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {displayName(it.pharmacyName, masked)}
                  </div>
                  <div>{getDeliveriesCount(it)}</div>
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.driverName ? displayName(it.driverName, masked) : "—"}
                  </div>
                  <div>{getEfficiencyDisplay(it)}</div>
                </button>
              );
            })}
            {!loading && !items.length && (
              <div style={{ padding: 12, fontSize: 13, color: "#444" }}>
                No results for current filters.
              </div>
            )}
          </div>
          <div
            style={{
              padding: 8,
              fontSize: 12,
              color: "#444",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid #eee",
              background: "#fff",
            }}
          >
            <div>
              showing {items.length} of {total} (page {page})
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => {
                  if (canPrev) {
                    setPage((p) => p - 1);
                    setTimeout(loadList, 0);
                  }
                }}
                disabled={!canPrev}
                style={{
                  border: "1px solid #ccc",
                  background: canPrev ? "#fff" : "#f5f5f5",
                  color: canPrev ? "#111827" : "#9ca3af",
                  padding: "4px 10px",
                  borderRadius: 6,
                  cursor: canPrev ? "pointer" : "default",
                }}
              >
                ← Prev 200
              </button>
              <button
                onClick={() => {
                  if (canNext) {
                    setPage((p) => p + 1);
                    setTimeout(loadList, 0);
                  }
                }}
                disabled={!canNext}
                style={{
                  border: "1px solid #ccc",
                  background: canNext ? "#fff" : "#f5f5f5",
                  color: canNext ? "#111827" : "#9ca3af",
                  padding: "4px 10px",
                  borderRadius: 6,
                  cursor: canNext ? "pointer" : "default",
                }}
              >
                Next 200 →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT */}
      <div style={{ overflowY: "auto", padding: 6 }}>
        {!selectedKey ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#444",
            }}
          >
            Select a route on the left…
          </div>
        ) : detailLoading ? (
          <div style={{ padding: 10, color: "#111" }}>Loading details…</div>
        ) : !selectedDoc ? (
          <div style={{ padding: 10, color: "crimson" }}>No detail available.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {/* Header */}
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: 12,
                background: "white",
                color: "#000",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div>
                  <b>Date:</b> {fmtDate(selectedDoc.deliveryDate)}
                </div>
                <div>
                  <b>Time of first delivery:</b>{" "}
                  {firstDeliveredAt
                    ? `${pad2(firstDeliveredAt.getHours())}:${pad2(
                        firstDeliveredAt.getMinutes()
                      )}`
                    : "—"}
                </div>
                <div>
                  <b>Pharmacy:</b> {displayName(selectedDoc.pharmacyName, masked)}
                </div>
                <div>
                  <b>Route ID:</b> {selectedDoc.deliveryRoundId}
                </div>
                <div>
                  <b>Driver:</b> {selectedDoc.driverName ? displayName(selectedDoc.driverName, masked) : "—"}
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <button
                    onClick={openMap}
                    style={{
                      background: "#10b981",
                      color: "#fff",
                      border: 0,
                      padding: "6px 10px",
                      borderRadius: 6,
                      cursor: "pointer",
                    }}
                  >
                    View map
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: "#111" }}>
                Stops with coords:{" "}
                <b>{selectedDoc?.roundSummary?.stopsWithCoords ?? "—"}</b>
              </div>
            </div>

            {/* Aggregates */}
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: 12,
                background: "white",
                color: "#000",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Deliveries
                  </div>
                  <div>
                    Success: <b>{deliveriesSummary?.successCount ?? "—"}</b>
                  </div>
                  <div>
                    Fail: <b>{deliveriesSummary?.failCount ?? "—"}</b>
                  </div>
                  <div>
                    Efficiency actual/predicted for route:{" "}
                    <b>
                      {tooShort
                        ? "n/a"
                        : agg?.efficiencyActualVsPredicted ?? "—"}
                    </b>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Distance / Time
                  </div>
                  <div>
                    Predicted distance:{" "}
                    <b>
                      {displayDistance(
                        agg?.totalPredictedDistanceKm ??
                          agg?.totalOsrmDistanceKm
                      ) ?? "—"}
                    </b>{" "}
                    <span
                      onClick={toggleDistanceUnit}
                      title={`Showing ${distanceUnit === "mi" ? "miles" : "kilometres"} — click to switch to ${distanceUnit === "mi" ? "kilometres" : "miles"}.`}
                      style={{
                        cursor: "pointer",
                        userSelect: "none",
                        textDecoration: "underline dotted",
                        color: "#4f46e5",
                        fontWeight: 600,
                      }}
                    >
                      {distanceUnit === "mi" ? "miles" : "km"} ⇄
                    </span>
                  </div>
                  <div>
                    Predicted drive time:{" "}
                    <b>{secondsToHM(agg?.totalPredictedSeconds ?? 0)}</b>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Route totals
                  </div>
                  <div>
                    Total time taken:{" "}
                    <b>
                      {tooShort
                        ? "n/a"
                        : secondsToHM(adjustedTotalActualSeconds)}
                    </b>
                  </div>
                </div>
              </div>
            </div>

            {/* Hops */}
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: 12,
                background: "white",
                color: "#000",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Hops</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>from</th>
                      <th style={th}>to</th>
                      <th
                        style={{ ...th }}
                        title="This is the predicted time between drops plus 120 seconds to allow for customer interaction"
                      >
                        Predicted secs
                      </th>
                      <th style={th}>Actual secs</th>
                      <th style={th}>Real time</th>
                      <th style={th}>Dist (m)</th>
                      <th style={th}>Outcome</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hops.map((h, i) => {
                      const fromRaw = h.fromLabel ?? h.fromSeq;
                      const toLabel = h.toLabel ?? h.toSeq;
                      const fromLabel =
                        h.isPharmacyToFirst ||
                        String(fromRaw) === "0" ||
                        Number(h.fromSeq) === -1
                          ? "P"
                          : fromRaw;

                      const predictedSecs = predictedSecsForHop(h);
                      const actualSecs = actualSecsForHop(h);

                      // Default real time: delivered time of the TO stop
                      let realTime = "—";

                      // If this is the last hop to pharmacy, fabricate time = lastDeliveredAt + predictedSecs
                      if (h.isLastToPharmacy && lastDeliveredAt && predictedSecs) {
                        const fake = new Date(lastDeliveredAt);
                        fake.setSeconds(fake.getSeconds() + predictedSecs);
                        realTime = `${pad2(fake.getHours())}:${pad2(
                          fake.getMinutes()
                        )}`;
                      } else {
                        const toSeqNum = Number(h.toSeq);
                        if (!Number.isNaN(toSeqNum)) {
                          const s = perStopBySeq.get(toSeqNum);
                          if (s?.deliveredAt) {
                            const dt = new Date(s.deliveredAt);
                            realTime = `${pad2(dt.getHours())}:${pad2(
                              dt.getMinutes()
                            )}`;
                          }
                        }
                      }

                      // Outcome from the TO stop if present
                      let outcome = "—";
                      const toSeqNum2 = Number(h.toSeq);
                      let toStop = null;
                      if (!Number.isNaN(toSeqNum2)) {
                        toStop = perStopBySeq.get(toSeqNum2);
                        if (typeof toStop?.success === "boolean") {
                          outcome = toStop.success ? "Success" : "Failure";
                        }
                      }

                      return (
                        <tr key={i}>
                          <td style={cellPad}>{fromLabel}</td>
                          <td style={cellPad}>{toLabel}</td>
                          <td style={cellPad}>{predictedSecs}</td>
                          <td style={cellPad}>
                            {Number.isFinite(actualSecs) ? actualSecs : "—"}
                          </td>
                          <td style={cellPad}>{realTime}</td>
                          <td style={cellPad}>{h.osrmDistanceM}</td>
                          <td style={cellPad}>{outcome}</td>
                          <td style={cellPad}>
                            {toStop && toStop.deliveredAt && (
                              <button
                                type="button"
                                onClick={() => openDriverLocationForStop(toStop)}
                                title="Show driver's recorded location around this delivery"
                                style={{
                                  cursor: "pointer",
                                  border: "1px solid #ccc",
                                  borderRadius: 6,
                                  background: "#f7f7f7",
                                  color: "#111827",
                                  padding: "2px 6px",
                                  fontSize: 12,
                                  lineHeight: 1,
                                }}
                              >
                                📍 Driver location
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!hops.length && (
                      <tr>
                        <td colSpan={8} style={{ ...cellPad, color: "#444" }}>
                          No hops
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stationary clusters */}
            <div
              style={{
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: 12,
                background: "white",
                color: "#000",
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Stationary clusters
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Start</th>
                      <th style={th}>End</th>
                      <th style={th}>Duration (min)</th>
                      <th style={th}>Points</th>
                      <th style={th}>Centroid</th>
                      <th style={th}>Drop</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clusters.map((c, i) => {
                      let dropLabel = "—";
                      if (c.atPharmacyWithin30m) {
                        dropLabel = "Pharmacy";
                      } else if (Number.isInteger(c.nearDeliverySeq)) {
                        dropLabel = Number(c.nearDeliverySeq) + 1;
                      } else if (c.nearDeliveryPatientId) {
                        const found = (perStop || []).find(
                          (s) =>
                            String(s.patientId) ===
                              String(c.nearDeliveryPatientId) &&
                            Number.isInteger(s.seq)
                        );
                        if (found) dropLabel = Number(found.seq) + 1;
                      }
                      return (
                        <tr key={i}>
                          <td style={cellPad}>
                            {c.start
                              ? new Date(c.start).toLocaleString("en-GB")
                              : "—"}
                          </td>
                          <td style={cellPad}>
                            {c.end
                              ? new Date(c.end).toLocaleString("en-GB")
                              : "—"}
                          </td>
                          <td style={cellPad}>{c.durationMin}</td>
                          <td style={cellPad}>{c.points}</td>
                          <td style={cellPad}>
                            {c?.centroid
                              ? `${c.centroid.lat?.toFixed?.(5)}, ${c.centroid.lon?.toFixed?.(5)}`
                              : "—"}
                          </td>
                          <td style={cellPad}>{dropLabel}</td>
                        </tr>
                      );
                    })}
                    {!clusters.length && (
                      <tr>
                        <td colSpan={6} style={{ ...cellPad, color: "#444" }}>
                          No clusters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <details
              style={{
                border: "1px solid #ddd",
                borderRadius: 10,
                padding: 12,
                background: "white",
                color: "#000",
              }}
            >
              <summary style={{ cursor: "pointer", fontWeight: 700 }}>
                Diagnostics
              </summary>
              <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", marginTop: 10 }}>
{JSON.stringify(
  {
    meta: {
      dbName: selectedDoc?.meta?.dbName,
      pharmacyName: displayName(selectedDoc?.meta?.pharmacyName, masked),
      driverName: displayName(selectedDoc?.meta?.driverName, masked),
    },
  },
  null,
  2
)}
              </pre>
            </details>
          </div>
        )}
      </div>

      {/* MAP MODAL */}
      {showMap && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              width: "90vw",
              height: "85vh",
              borderRadius: 10,
              overflow: "hidden",
              display: "grid",
              gridTemplateRows: "auto 1fr",
            }}
          >
            {/* Toolbar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 12px",
                borderBottom: "1px solid #eee",
                flexWrap: "wrap",
              }}
            >
              <b>Map</b>
              <label style={{ marginLeft: 12 }}>
                <input
                  type="checkbox"
                  checked={mapOptions.deliveries}
                  onChange={(e) =>
                    setMapOptions((o) => ({
                      ...o,
                      deliveries: e.target.checked,
                    }))
                  }
                />{" "}
                Deliveries
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={mapOptions.actual}
                  onChange={(e) =>
                    setMapOptions((o) => ({ ...o, actual: e.target.checked }))
                  }
                />{" "}
                Actual route
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={mapOptions.stationary}
                  onChange={(e) =>
                    setMapOptions((o) => ({
                      ...o,
                      stationary: e.target.checked,
                    }))
                  }
                />{" "}
                Stationary episodes
              </label>
              <div style={{ marginLeft: "auto" }}>
                <button
                  onClick={() => setShowMap(false)}
                  style={{
                    background: "#ef4444",
                    color: "#fff",
                    border: 0,
                    padding: "6px 10px",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>

            {/* Map */}
            <div style={{ position: "relative" }}>
              <MapContainer
                key={mapKey}
                center={[51.5, -0.1]}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds points={mapPoints} />

                {/* Pharmacy pin */}
                {mapData.pharmacy && (
                  <Marker
                    position={[mapData.pharmacy.lat, mapData.pharmacy.long]}
                    icon={houseIcon}
                  >
                    <Popup>
                      <b>Pharmacy</b>
                    </Popup>
                  </Marker>
                )}

                {/* Deliveries (perStop) */}
                {mapOptions.deliveries &&
                  (perStop || []).map((s, idx) => {
                    const has = s?.coords?.lat && s?.coords?.lon;
                    if (!has) return null;
                    const dt = s.deliveredAt ? new Date(s.deliveredAt) : null;
                    const label = dt
                      ? `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`
                      : `#${Number(s.seq) + 1}`;
                    return (
                      <React.Fragment key={`drop-${idx}`}>
                        <CircleMarker
                          center={[s.coords.lat, s.coords.lon]}
                          radius={6}
                          fillOpacity={0.9}
                        >
                          <Popup>
                            <b>Drop {Number(s.seq) + 1}</b>
                            <br />
                            Time: {label}
                          </Popup>
                        </CircleMarker>
                        <Marker
                          position={[s.coords.lat, s.coords.lon]}
                          icon={L.divIcon({
                            className: "delivery-time-label",
                            html: `<div style="position:absolute;top:-18px;left:calc(50% + 10px);background:white;padding:2px 4px;border:1px solid gray;border-radius:3px;font-size:10px">${label}</div>`,
                            iconSize: [0, 0],
                          })}
                        />
                      </React.Fragment>
                    );
                  })}

                {/* Actual route polyline */}
                {mapOptions.actual &&
                  Array.isArray(mapData.driverLogs) &&
                  mapData.driverLogs.length >= 2 && (
                    <Polyline
                      positions={mapData.driverLogs}
                      pathOptions={{ weight: 3 }}
                    />
                  )}

                {/* Stationary episodes as sized blobs */}
                {mapOptions.stationary &&
                  (clusters || []).map((c, i) => {
                    if (!c?.centroid?.lat || !c?.centroid?.lon) return null;
                    const radius = Math.max(
                      8,
                      Math.min(30, Number(c.durationMin || 0) * 2)
                    );
                    return (
                      <CircleMarker
                        key={`st-${i}`}
                        center={[c.centroid.lat, c.centroid.lon]}
                        radius={radius}
                        pathOptions={{ color: "purple" }}
                      >
                        <Popup>
                          <b>Stationary</b>
                          <br />
                          Duration: {c.durationMin} min
                        </Popup>
                      </CircleMarker>
                    );
                  })}
              </MapContainer>
            </div>
          </div>
        </div>
      )}

      {/* DRIVER LOCATION MODAL (per-delivery) */}
      {showDriverLocModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowDriverLocModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: 10,
              width: "min(720px, 92vw)",
              maxHeight: "88vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ color: "#000" }}>
                <div style={{ fontWeight: 700 }}>
                  Driver location — Drop {driverLocData.stopLabel ?? "—"}
                </div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  Outcome: <b>{driverLocData.outcome}</b>
                  {driverLocData.deliveredAt && (
                    <>
                      {" "}
                      • Recorded:{" "}
                      <b>
                        {new Date(driverLocData.deliveredAt).toLocaleString("en-GB")}
                      </b>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDriverLocModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#111827",
                  fontSize: 20,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            <div style={{ padding: "8px 16px", fontSize: 12, color: "#333" }}>
              <span style={{ marginRight: 14 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#2563eb",
                    marginRight: 5,
                  }}
                />
                Driver location (driver_logs, closest 5 in time)
              </span>
              {driverLocData.deliveryLogPoint && (
                <span style={{ marginRight: 14 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: "#dc2626",
                      marginRight: 5,
                    }}
                  />
                  Location recorded on the delivery itself
                </span>
              )}
              {driverLocData.patientCoords && (
                <span>
                  <span style={{ display: "inline-block", marginRight: 5, fontWeight: 900, color: "#111827" }}>
                    ▼
                  </span>
                  Patient address (reference) — the black arrow's <b>tip</b> points at the patient's recorded address; it's a fixed reference point, not a live location
                </span>
              )}
            </div>

            {driverLocLoading && (
              <div style={{ padding: "0 16px 12px", color: "#444" }}>Loading…</div>
            )}
            {!driverLocLoading && driverLocError && (
              <div style={{ padding: "0 16px 12px", color: "#b91c1c" }}>
                {driverLocError}
              </div>
            )}

            <div style={{ height: 420, padding: "0 16px 16px" }}>
              <MapContainer
                key={`driverloc-${driverLocData.stopLabel}-${driverLocData.driverPoints.length}`}
                center={[51.5, -0.1]}
                zoom={13}
                style={{ height: "100%", width: "100%", borderRadius: 8 }}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds
                  points={[
                    ...driverLocData.driverPoints.map((p) => [p.lat, p.long]),
                    ...(driverLocData.deliveryLogPoint ? [driverLocData.deliveryLogPoint] : []),
                    ...(driverLocData.patientCoords ? [driverLocData.patientCoords] : []),
                  ]}
                />

                {/* Patient address reference pin — a solid black arrow, tip
                    pinned exactly on the coordinate. Deliberately a Marker
                    (not a CircleMarker): Leaflet draws Markers in a pane
                    above CircleMarkers, so this always sits on top of the
                    numbered driver-location dots instead of being buried
                    under them, and the dark, high-contrast colour (vs. the
                    old pale grey dot) keeps it readable over any tile. */}
                {driverLocData.patientCoords && (
                  <Marker
                    position={driverLocData.patientCoords}
                    icon={L.divIcon({
                      className: "patient-address-arrow",
                      html: `<svg width="34" height="46" viewBox="0 0 34 46" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
                        <path d="M17 2 L17 27" stroke="#ffffff" stroke-width="7" stroke-linecap="round"/>
                        <path d="M17 2 L17 27" stroke="#111827" stroke-width="4" stroke-linecap="round"/>
                        <polygon points="17,44 5,25 29,25" fill="#111827" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/>
                      </svg>`,
                      iconSize: [34, 46],
                      iconAnchor: [17, 44], // tip of the arrow, not the icon's centre
                    })}
                    zIndexOffset={1000}
                  >
                    <Popup>
                      <b>Patient address</b>
                      <br />
                      Reference point only — the arrow tip marks the recorded address, not a live location.
                    </Popup>
                  </Marker>
                )}

                {/* Driver's path around the delivery (driver_logs) */}
                {driverLocData.driverPoints.length >= 2 && (
                  <Polyline
                    positions={driverLocData.driverPoints.map((p) => [p.lat, p.long])}
                    pathOptions={{ weight: 2, color: "#2563eb", dashArray: "4 4" }}
                  />
                )}
                {driverLocData.driverPoints.map((p, idx) => (
                  <Marker
                    key={`driverpt-${idx}`}
                    position={[p.lat, p.long]}
                    icon={L.divIcon({
                      className: "driver-log-point",
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:#2563eb;color:white;font-size:11px;font-weight:700;border:2px solid white;box-shadow:0 0 2px rgba(0,0,0,0.5);">${idx + 1}</div>`,
                      iconSize: [22, 22],
                      iconAnchor: [11, 11],
                    })}
                  >
                    <Popup>
                      <b>Driver location {idx + 1}</b>
                      <br />
                      {new Date(p.createdAt).toLocaleString("en-GB")}
                    </Popup>
                  </Marker>
                ))}

                {/* Location recorded on the delivery_logs document itself */}
                {driverLocData.deliveryLogPoint && (
                  <Marker
                    position={driverLocData.deliveryLogPoint}
                    icon={L.divIcon({
                      className: "delivery-log-point",
                      html: `<div style="width:20px;height:20px;border-radius:50%;background:#dc2626;border:2px solid white;box-shadow:0 0 2px rgba(0,0,0,0.5);"></div>`,
                      iconSize: [20, 20],
                      iconAnchor: [10, 10],
                    })}
                  >
                    <Popup>
                      <b>Recorded on delivery</b>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteAnalysis;